import {useState, useEffect} from 'react';
import ChatLayout from './components/ChatLayout';
import {SessionMeta} from './lib/types';
import ChatView from './features/ChatView';
import {modelIdFromUrl} from './lib/modelRoute';
import {CONFIG} from './lib/config';
import {validateApiKey} from './lib/apiKeyValidation';
import ConsentGate, {hasAcceptedConsent} from './components/ConsentGate';

function getBrowserId(): string {
  const key = 'lapathoniia-uid';
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).slice(2);
    localStorage.setItem(key, id);
  }
  return id;
}

export default function App() {
  const [userId] = useState(getBrowserId);
  const [requestedModelId] = useState(() => modelIdFromUrl(window.location));
  const [apiKey, setApiKey] = useState<string>(() => localStorage.getItem('lapathoniia-api-key') || '');
  const [showKeyInput, setShowKeyInput] = useState(false);
  const [keyDraft, setKeyDraft] = useState('');
  const [keyValidating, setKeyValidating] = useState(false);
  const [keyError, setKeyError] = useState('');
  const [consentAccepted, setConsentAccepted] = useState(hasAcceptedConsent);

  // Validate stored key on startup — clear if it no longer exists in LiteLLM
  useEffect(() => {
    const stored = localStorage.getItem('lapathoniia-api-key');

    if (stored) {
      // Validate existing saved key
      validateApiKey(CONFIG.backendUrl, stored)
        .then(status => {
          if (status === 'invalid') {
            localStorage.removeItem('lapathoniia-api-key');
            setApiKey('');
            tryAutoLoginFromCookie();
          }
        })
        .catch(() => {});
      return;
    }

    // No saved key — try auto-login from lapa_session cookie (set by dashboard)
    tryAutoLoginFromCookie();
  }, []);

  function tryAutoLoginFromCookie() {
    const match = document.cookie.match(/(?:^|;\s*)lapa_session=([^;]+)/);
    if (!match) return;
    const clerkUserId = decodeURIComponent(match[1]).trim();
    if (!clerkUserId) return;
    fetch(`${CONFIG.backendUrl}/auth/me?clerk_user_id=${encodeURIComponent(clerkUserId)}`)
      .then(r => r.ok ? r.json() : null)
      .then((d: { litellm_key?: string } | null) => {
        if (d?.litellm_key) {
          localStorage.setItem('lapathoniia-api-key', d.litellm_key);
          setApiKey(d.litellm_key);
        }
      })
      .catch(() => {});
  }

  const handleLogout = () => { setKeyError(''); setShowKeyInput(true); };
  const handleLogin = (key: string) => {
    localStorage.setItem('lapathoniia-api-key', key);
    setApiKey(key);
    setShowKeyInput(false);
    setKeyDraft('');
    setKeyError('');
  };
  const handleUseDefault = () => {
    localStorage.removeItem('lapathoniia-api-key');
    setApiKey('');
    setShowKeyInput(false);
    setKeyDraft('');
    setKeyError('');
  };

  const handleSubmitKey = async () => {
    const key = keyDraft.trim();
    if (!key) return;
    setKeyValidating(true);
    setKeyError('');
    try {
      const status = await validateApiKey(CONFIG.backendUrl, key);
      if (status === 'valid') {
        handleLogin(key);
      } else if (status === 'invalid') {
        setKeyError('Ключ не знайдено в системі. Перевірте правильність або отримайте новий ключ.');
      } else if (status === 'rate_limited') {
        setKeyError('Забагато спроб перевірки. Зачекайте хвилину та спробуйте ще раз.');
      } else {
        setKeyError('Перевірка ключа тимчасово недоступна. Спробуйте ще раз пізніше.');
      }
    } catch {
      setKeyError('Не вдалося перевірити ключ. Перевірте підключення до інтернету.');
    } finally {
      setKeyValidating(false);
    }
  };

  return (
    <>
      {!consentAccepted && (
        <ConsentGate onAccept={() => setConsentAccepted(true)} />
      )}
      <ChatLayout
      userId={userId}
      requestedModelId={requestedModelId}
      apiKey={apiKey}
      onLogout={handleLogout}
      renderView={(session: SessionMeta, onUpdate: (s: SessionMeta) => void, onDirtyChange: (dirty: boolean) => void) =>
        <ChatView key={session.id} session={session} onUpdateSession={onUpdate} onOcrDirtyChange={onDirtyChange} userId={userId} apiKey={apiKey} />
      }
      keyModal={showKeyInput ? {
        draft: keyDraft,
        setDraft: (v) => { setKeyDraft(v); setKeyError(''); },
        onSubmit: handleSubmitKey,
        onDefault: handleUseDefault,
        onClose: () => { setShowKeyInput(false); setKeyError(''); setKeyDraft(''); },
        validating: keyValidating,
        error: keyError,
      } : undefined}
    />
    </>
  );
}
